import { FC, useState, useEffect, useRef, useContext, useMemo } from 'react';
import { Modal, Stack, Text, ScrollArea, Radio, Checkbox } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  PedigreeRelationshipNodeData,
  PedigreeRelationshipNodeType,
} from '../types';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { isEqual, isNil } from 'lodash';
import { PedigreeContext } from '../context/PedigreeContext';
import { useGraphData } from '../hooks/useGraphData';

// -----------------------------------------------------------------------------
const DEFAULT_RELATIONSHIP_DATA: PedigreeRelationshipNodeData = {
  label: '',
  relationshipType: 'partnership',
  isConsanguineous: false,
};

// -----------------------------------------------------------------------------
interface RelationshipModalProps {
  onClose?: () => void;
}

// -----------------------------------------------------------------------------
export const RelationshipModal: FC<RelationshipModalProps> = ({ onClose }) => {
  const { t } = useTranslation('pedigree');
  const [formData, setFormData] = useState<PedigreeRelationshipNodeData>(
    DEFAULT_RELATIONSHIP_DATA
  );

  const baselineRef = useRef<PedigreeRelationshipNodeData | null>(null);
  const formActionsRef = useRef<FormActionButtonsRef>(null);

  const { selectedNodeId } = useContext(PedigreeContext) || {};
  const { getNodeType, getNodeData, editNodeData } = useGraphData();

  const isOpen = useMemo(
    () =>
      !isNil(selectedNodeId) && getNodeType(selectedNodeId) === 'relationship',
    [selectedNodeId, getNodeType]
  );

  const relationship = useMemo(
    () => (isOpen ? getNodeData(selectedNodeId) : null),
    [isOpen, getNodeData, selectedNodeId]
  );

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!relationship) {
      return;
    }

    setFormData({ ...relationship });
    if (isOpen) {
      baselineRef.current = { ...relationship };
    }
  }, [isOpen, relationship]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isOpen) {
      return;
    }

    baselineRef.current = null;
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  if (!isOpen) {
    return null;
  }

  // ---------------------------------------------------------------------------
  const handleSubmit = () => {
    editNodeData(selectedNodeId || -1, formData);
    handleClose();
  };

  // ---------------------------------------------------------------------------
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }

    try {
      return !isEqual(formData, baselineRef.current);
    } catch {
      return true;
    }
  };

  // ---------------------------------------------------------------------------
  const handleClose = () => {
    onClose?.();
  };

  // ---------------------------------------------------------------------------
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormData({ ...baselineRef.current });
    } else {
      setFormData(DEFAULT_RELATIONSHIP_DATA);
    }
  };
  // ---------------------------------------------------------------------------
  const modalTitle = relationship
    ? t('relationshipModal.editTitle')
    : t('relationshipModal.createTitle');

  const submitButtonText = t('relationshipModal.saveChanges');

  // ---------------------------------------------------------------------------
  return (
    <>
      <Modal
        opened={isOpen}
        onClose={() => formActionsRef.current?.handleGuardedClose()}
        title={modalTitle}
        size="md"
        centered
        scrollAreaComponent={ScrollArea.Autosize}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Stack gap="md">
          {' '}
          {/* Relationship Type */}
          <Radio.Group
            label={t('relationshipModal.relationshipTypeLabel')}
            value={formData.relationshipType || 'partnership'}
            onChange={(value) =>
              setFormData({
                ...formData,
                relationshipType: value as PedigreeRelationshipNodeType,
              })
            }
          >
            <Stack gap="xs" mt="xs">
              <Radio
                value="partnership"
                label={t('relationshipModal.relationshipTypes.partnership')}
              />
              <Radio
                value="separated"
                label={t('relationshipModal.relationshipTypes.separated')}
              />
            </Stack>
          </Radio.Group>
          {/* Consanguineous Checkbox */}
          <Checkbox
            label={t('relationshipModal.consanguineousLabel')}
            description={t('relationshipModal.consanguineousDescription')}
            checked={formData.isConsanguineous || false}
            onChange={(e) =>
              setFormData({
                ...formData,
                isConsanguineous: e.currentTarget.checked,
              })
            }
          />{' '}
          {/* Information text */}
          <Text size="sm" c="dimmed">
            <Text span fw={500}>
              {t('relationshipModal.informationText.partnershipLabel')}
            </Text>{' '}
            {t('relationshipModal.informationText.partnershipDescription')}
            <br />
            <Text span fw={500}>
              {t('relationshipModal.informationText.separatedLabel')}
            </Text>{' '}
            {t('relationshipModal.informationText.separatedDescription')}
            <br />
            <Text span fw={500}>
              {t('relationshipModal.informationText.consanguineousLabel')}
            </Text>{' '}
            {t('relationshipModal.informationText.consanguineousDescription')}
          </Text>
          {/* Buttons */}
          <FormActionButtons
            ref={formActionsRef}
            onSave={handleSubmit}
            onDiscard={handleDiscardChanges}
            onClose={handleClose}
            hasUnsavedChanges={hasUnsavedChanges()}
            saveLabel={submitButtonText}
          />
        </Stack>
      </Modal>
    </>
  );
};
